import * as THREE from 'three';
import { STAGE } from '@vrlearning/lesson-schema';

/**
 * A floor that shows no sun shadow: a ground-projected 360° photo as package worlds make it -- a
 * kit texture that glows, its base colour painted black by a kleur block (`colour #000000`,
 * `emissive #ffffff`, `glow 3`), so all of it is its own light and the sun adds nothing, nor
 * takes anything away where a shadow falls. "Op Mars"'s marsrover hovered on one (Pieter, on a
 * Quest 3, Oct 2026): the sun's shadow fell on the lit ground beneath the photo, out of sight.
 */

/** Darker than this base colour (each channel, linear) and glowing: lit by itself alone. */
const BLACK = 0.03;

/** A material the sun cannot shade: unlit (MeshBasic), or black and glowing. Light-look additive parts are not floors. */
export function unlitMaterial(material: THREE.Material): boolean {
  if (material.blending === THREE.AdditiveBlending) return false;
  if (material instanceof THREE.MeshBasicMaterial) return true;
  if (material instanceof THREE.MeshStandardMaterial) {
    const c = material.color;
    const e = material.emissive;
    return (
      Math.max(c.r, c.g, c.b) < BLACK &&
      material.emissiveIntensity > 0 &&
      Math.max(e.r, e.g, e.b) > 0
    );
  }
  return false;
}

const box = new THREE.Box3();

/**
 * Whether `mesh` lies as a floor of the stage: its lowest point at the stage floor (within 10 cm of
 * y = 0) and reaching past the stage's 2 m floor on every side (a photo's floor out to the horizon,
 * its 10 m rim). Its world matrix as it stands.
 */
export function stageFloorShaped(mesh: THREE.Mesh): boolean {
  if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
  box.copy(mesh.geometry.boundingBox!).applyMatrix4(mesh.matrixWorld);
  const reach = STAGE.floor;
  return (
    Math.abs(box.min.y) <= 0.1 &&
    box.min.x < -reach &&
    box.max.x > reach &&
    box.min.z < -reach &&
    box.max.z > reach
  );
}

/** Whether a floor-shaped mesh shows no sun shadow now: seen, and unlit (its kleur may change). */
export function unlitNow(mesh: THREE.Mesh): boolean {
  if (!mesh.visible) return false;
  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  return materials.some(unlitMaterial);
}
